// Remembered voice-message playback positions, persisted in localStorage under
// one key. Injectable storage + clock so it's testable in node (mocha).
//
// ponytail: keyed by the `src` string (the transformed blob URL, stable per
// message file) instead of threading messageId through AudioPlayer. Capped at
// ~300 entries, oldest-by-`at` dropped; all storage access is try/caught so
// quota / private-mode just no-ops.

const KEY = 'voiceMessagePlaybackPositions'
const CAP = 300

type Entry = { t: number; at: number }
type StoreShape = Record<string, Entry>
type StorageLike = Pick<Storage, 'getItem' | 'setItem'>

export function makePositionStore(
  storage: StorageLike,
  now: () => number = () => Date.now()
) {
  const read = (): StoreShape => {
    try {
      // JSON.parse('null'/'5'/'[1]') all succeed but aren't our object shape;
      // returning them would make getPosition/setPosition throw. Validate, then
      // the next setPosition write() self-heals the corrupt key.
      const v = JSON.parse(storage.getItem(KEY) || '{}')
      return v !== null && typeof v === 'object' && !Array.isArray(v)
        ? (v as StoreShape)
        : {}
    } catch {
      return {}
    }
  }
  const write = (s: StoreShape): void => {
    try {
      storage.setItem(KEY, JSON.stringify(s))
    } catch {
      // storage may be blocked (private mode / quota); positions are optional
    }
  }
  // Object URLs (blob:) are per-session and change every time — persisting a
  // position under one is pure garbage that never matches again. The recorder's
  // preview player plays a blob: URL through the same CustomAudioControls, so
  // guard here (the one place all writers route through) rather than in each.
  const isEphemeral = (src: string): boolean => src.startsWith('blob:')
  return {
    getPosition(src: string): number | null {
      if (isEphemeral(src)) {
        return null
      }
      const e = read()[src]
      return e ? e.t : null
    },
    setPosition(src: string, t: number): void {
      if (isEphemeral(src)) {
        return
      }
      const s = read()
      s[src] = { t, at: now() }
      const keys = Object.keys(s)
      if (keys.length > CAP) {
        keys.sort((a, b) => (s[a]?.at ?? 0) - (s[b]?.at ?? 0))
        for (const k of keys.slice(0, keys.length - CAP)) {
          delete s[k]
        }
      }
      write(s)
    },
    clearPosition(src: string): void {
      const s = read()
      if (src in s) {
        delete s[src]
        write(s)
      }
    },
  }
}

// Browser default; a no-op storage keeps import-time safe where localStorage is
// absent (e.g. node test harnesses importing this module transitively).
export const positionStore =
  typeof localStorage !== 'undefined'
    ? makePositionStore(localStorage)
    : makePositionStore({ getItem: () => null, setItem: () => {} })
